import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Search, X, Loader2, Edit3 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { logFoodAPI } from '../../../utils/api';
import RecentlyLoggedList from '../../../components/logfood/RecentlyLoggedList';
import FoodResultRow from '../../../components/logfood/FoodResultRow';
import FallbackChips from '../../../components/logfood/FallbackChips';
import { searchItemToFood } from '../../../utils/logFoodMappers';

/**
 * Manual tab — ad-hoc entries + the user's saved custom foods.
 *
 * "Create manual entry" reuses the existing openManualEntry() flow from
 * LogFood.jsx (parent passes `onCreateManual`).
 */
export default function ManualTab({
  recents,
  recentsLoading,
  onSelectFood,
  onSelectRecent,
  onQuickLog,
  onQuickLogRecent,
  onCreateManual,
  onTryTab,
  onSearchAllRecents,
}) {
  const [query, setQuery] = useState('');
  const [savedFoods, setSavedFoods] = useState([]);
  const [matchingRecents, setMatchingRecents] = useState([]);
  const [fallback, setFallback] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const doFetch = useCallback(function (q) {
    setLoading(true);
    const params = { limit: 30 };
    if (q && q.trim()) params.q = q.trim();
    const recentsPromise =
      q && q.trim()
        ? logFoodAPI.getRecentFoods({ scope: 'manual', q: q.trim(), limit: 3 })
            .catch(function () { return { data: [] }; })
        : Promise.resolve({ data: [] });
    Promise.all([
      logFoodAPI.searchMyFoods(params).catch(function () { return { data: { items: [], total: 0 } }; }),
      recentsPromise,
    ]).then(function (arr) {
      const data = (arr[0] && arr[0].data) || {};
      setSavedFoods(Array.isArray(data.items) ? data.items : []);
      setFallback(data.fallback_suggestion || null);
      setMatchingRecents(Array.isArray(arr[1].data) ? arr[1].data.slice(0, 3) : []);
      setLoading(false);
    });
  }, []);

  useEffect(function () {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(function () { doFetch(query); }, 300);
    return function () { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, doFetch]);

  const isLanding = !query.trim();
  const isEmpty =
    !loading &&
    savedFoods.length === 0 &&
    (recents || []).length === 0 &&
    isLanding;

  return (
    <div className="flex flex-col" data-testid="manual-tab">
      <div className="px-4 pt-3 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={function (e) { setQuery(e.target.value); }}
            placeholder="Search your saved foods..."
            className="w-full pl-9 pr-9 py-2.5 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="manual-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={function () { setQuery(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 size-5 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 flex items-center justify-center"
              aria-label="Clear search"
              data-testid="manual-search-clear"
            >
              <X className="size-3" />
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-3">
        <Button
          variant="default"
          className="w-full gap-2"
          onClick={function () { onCreateManual && onCreateManual(query.trim() || null); }}
          data-testid="manual-create-btn"
        >
          <Edit3 className="size-4" />
          {query.trim()
            ? '＋ Create manual entry "' + query.trim() + '"'
            : '＋ Create manual entry'}
        </Button>
      </div>

      {isEmpty && (
        <div className="px-4 py-12 text-center text-sm text-muted-foreground">
          Track foods that aren't in our database — type the macros directly.
        </div>
      )}

      {isLanding && !isEmpty && (
        <RecentlyLoggedList
          items={recents}
          onSelect={onSelectRecent}
          onQuickLog={onQuickLogRecent}
          emptyMessage={recentsLoading ? 'Loading…' : null}
        />
      )}

      {!isLanding && matchingRecents.length > 0 && !loading && (
        <RecentlyLoggedList
          items={matchingRecents}
          onSelect={onSelectRecent}
          onQuickLog={onQuickLogRecent}
          trailingAction={
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={function () { setQuery(''); }}
              data-testid="manual-search-all-recents"
            >
              Show all recents →
            </button>
          }
        />
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && savedFoods.length > 0 && (
        <>
          <div className="px-4 py-2 mt-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Saved Custom Foods
            </h3>
          </div>
          <ul className="divide-y divide-border">
            {savedFoods.map(function (item) {
              return (
                <FoodResultRow
                  key={item.id || item.external_id}
                  item={item}
                  onSelect={function () { onSelectFood(searchItemToFood(item)); }}
                  onQuickLog={function () { onQuickLog(searchItemToFood(item)); }}
                />
              );
            })}
          </ul>
        </>
      )}

      {!loading && !isLanding && savedFoods.length === 0 && fallback && (
        <FallbackChips
          suggestion={fallback}
          query={query}
          onTryTab={onTryTab}
          onCreateManual={onCreateManual}
        />
      )}
    </div>
  );
}
